import React from 'react';
import { Link } from 'react-router-dom';
import './IntelligenceServices.css';

const IntelligenceServices = () => {
  const services = [
    {
      title: 'Dashboards & Reporting',
      description: 'Executive dashboards with locked KPI definitions, drill-downs, and role-based access.',
      features: [
        'Locked KPI definitions',
        'Interactive drill-downs',
        'Role-based access control',
        'Live data refresh',
        'Scheduled report distribution',
        'Executive & team scorecards'
      ]
    },
    {
      title: 'Automation & AI',
      description: 'AI-driven efficiencies that reduce manual work and accelerate decision-making.',
      features: [
        'Data cleaning & enrichment',
        'Automated routing & triggers',
        'CRM/ERP workflow automation',
        'Anomaly detection & flagging',
        'Trend summarization',
        'Reduced repetitive tasks'
      ]
    },
    {
      title: 'Predictive Analytics',
      description: 'Forward-looking insights for revenue forecasting, demand planning, and scenario modeling.',
      features: [
        'Revenue & demand forecasting',
        'Churn prediction',
        'Scenario modeling',
        'Cohort analysis',
        'Actionable recommendations',
        'Strategic planning support'
      ]
    }
  ];

  return (
    <div className="intelligence-services-page">
      <section className="intelligence-hero">
        <div className="intelligence-hero-container">
          <div className="intelligence-hero-content">
            <h1 className="intelligence-hero-title">Business Intelligence Services</h1>
            <p className="intelligence-hero-description">
              Turn data into dashboards, automation, and predictive insights. Because data only matters when it drives action.
            </p>
            <p className="intelligence-hero-subtitle">
              From executive dashboards with live KPIs to AI-driven automation and predictive analytics, we help you move from information to decisions—faster.
            </p>
            <Link to="/contact" className="btn-intelligence-hero">Speak to an Expert</Link>
          </div>
          <div className="intelligence-hero-image">
            <img 
              src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80" 
              alt="Business Intelligence Dashboard" 
            />
          </div>
        </div>
      </section>

      <section className="intelligence-intro">
        <div className="intelligence-container">
          <h2 className="intelligence-section-title">Our Intelligence Capabilities</h2>
          <p className="intelligence-intro-text">
            Transform your data into actionable insights with dashboards, automation, and predictive analytics
          </p>
        </div>
      </section>

      <section className="intelligence-services-grid">
        <div className="intelligence-container">
          <div className="intelligence-cards-grid">
            {services.map((service, index) => (
              <div key={index} className="intelligence-capability-card">
                <h3 className="intelligence-capability-title">{service.title}</h3>
                <p className="intelligence-capability-description">{service.description}</p>
                <ul className="intelligence-capability-features">
                  {service.features.map((feature, idx) => (
                    <li key={idx}>{feature}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="intelligence-value">
        <div className="intelligence-container">
          <h2 className="intelligence-section-title">The VerTech BI Difference</h2>
          <div className="value-grid">
            <div className="value-card">
              <div className="value-icon">⚡</div>
              <h3 className="value-title">Actionable Insights</h3>
              <p className="value-description">
                We focus on insights that lead to concrete actions, not just pretty charts that sit unused.
              </p>
            </div>
            <div className="value-card">
              <div className="value-icon">🎯</div>
              <h3 className="value-title">Business-Focused</h3>
              <p className="value-description">
                Our analytics align with your business goals and KPIs, not generic metrics that don't matter.
              </p>
            </div>
            <div className="value-card">
              <div className="value-icon">🔄</div>
              <h3 className="value-title">Automation First</h3>
              <p className="value-description">
                We automate data collection, processing, and reporting so you always have current information.
              </p>
            </div>
            <div className="value-card">
              <div className="value-icon">📈</div>
              <h3 className="value-title">Predictive Analytics</h3>
              <p className="value-description">
                Look forward, not backward, with predictive models that anticipate trends and opportunities.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="intelligence-process">
        <div className="intelligence-container">
          <h2 className="intelligence-section-title">Our BI Implementation Process</h2>
          <div className="process-timeline">
            <div className="process-item">
              <div className="process-marker">1</div>
              <div className="process-content">
                <h3 className="process-title">Data Assessment</h3>
                <p className="process-description">
                  We evaluate your current data sources, quality, and accessibility to understand what's available 
                  and what needs improvement.
                </p>
              </div>
            </div>
            <div className="process-item">
              <div className="process-marker">2</div>
              <div className="process-content">
                <h3 className="process-title">Requirements Definition</h3>
                <p className="process-description">
                  Working with stakeholders, we define what questions need answering and what decisions the 
                  analytics will support.
                </p>
              </div>
            </div>
            <div className="process-item">
              <div className="process-marker">3</div>
              <div className="process-content">
                <h3 className="process-title">Dashboard Development</h3>
                <p className="process-description">
                  We build interactive dashboards and reports that present data in clear, actionable formats 
                  tailored to each audience.
                </p>
              </div>
            </div>
            <div className="process-item">
              <div className="process-marker">4</div>
              <div className="process-content">
                <h3 className="process-title">Training & Rollout</h3>
                <p className="process-description">
                  Your team learns how to use and interpret the analytics, with ongoing support to ensure 
                  maximum value and adoption.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="intelligence-cta">
        <div className="intelligence-container">
          <h2 className="intelligence-cta-title">Ready to Turn Data Into Strategic Advantage?</h2>
          <p className="intelligence-cta-description">
            Let's discuss how business intelligence can drive better decisions and automate your workflows.
          </p>
          <a href="/contact" className="btn-intelligence-primary">Schedule a Consultation</a>
        </div>
      </section>
    </div>
  );
};

export default IntelligenceServices;
